import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, GiftIcon, CheckCircle2Icon, ChevronDownIcon, LockIcon, BriefcaseIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
import { trackAddToCart } from '../../utils/metaPixel';
import { getCheckoutUrl } from '../../utils/checkoutUrl';
const bonusDetails = [{
  intro: 'Este es tu seguro para empezar sin miedo. Antes de coser cualquier prenda, vas a saber exactamente qué revisar, qué riesgos existen y cómo evitar los errores que más tiempo y dinero cuestan.',
  bullets: ['Diagnóstico Express (2 minutos): detecta si la prenda es segura antes de tocarla con la máquina', 'Los 12 errores más comunes en alteraciones y su solución inmediata (ruedo torcido, zipper ondulado, tela fruncida y más)', 'Pruebas rápidas antes de coser para no arriesgar la prenda del cliente', 'Control de calidad en 60 segundos antes de entregar', 'Plan de rescate: qué decirle al cliente si algo salió mal (guión corto, sin perder autoridad)', 'Checklist imprimible "Antes de Coser" (pégala cerca de tu máquina)', 'Checklist imprimible "Antes de Entregar" (para que nada se te escape)', 'Plantilla de Tarjeta de Cuidados lista para imprimir y entregar con cada prenda'],
  closingLine: 'Porque no hay nada peor que entregar algo que no quedó bien. Y no hay nada mejor que saber, desde el principio, que tienes todo bajo control.'
}, {
  intro: 'Coser bien no es suficiente si cuando te preguntan "¿cuánto cobras?" te tiembla la voz. Vas a tener mensajes listos para copiar y pegar en WhatsApp, texto o en persona.',
  bullets: ['6 scripts para responder "¿cuánto cobras?" en diferentes situaciones (precio directo, con rango, urgente, cliente que quiere descuento)', 'Scripts para pedir fotos y medidas sin sonar exigente', 'Scripts para agendar y confirmar entrega sin perseguir al cliente', 'Scripts para poner límites con respeto y sin culpa', 'Scripts anti-quejas: cómo responder si algo no quedó como esperaban, sin pelear y sin perder autoridad', 'Guión de 30 segundos para hablar en persona y sonar profesional desde el primer día', 'Hoja de personalización para ajustar cada script a tu tono y estilo', 'Pack "copiar y pegar" en texto plano para guardar en el teléfono y usar hoy mismo'],
  closingLine: 'Porque el dinero que pierdes no siempre es por coser mal. A veces es por no saber cómo pedir lo que mereces.'
}, {
  intro: 'No necesitas un taller. No necesitas un logo caro. Solo necesitas verte confiable. Cuando una clienta ve orden, entrega limpia y un pequeño sistema... llegan más recomendaciones y menos regateo.',
  bullets: ['Setup Express: cómo convertir tu mesa de comedor en una estación pro en 30 minutos con lo que ya tienes', 'Sistema Anti-Confusión: 3 estados (Por Hacer / Listo / Entregado) para que nunca mezcles prendas', 'Lista de compra mínima ordenada por prioridad (sin gastar de más)', 'Kit de entrega en 60 segundos: dobla, empaca y entrega como boutique', 'Plantilla: Etiqueta de prenda (lista para imprimir)', 'Plantilla: Tarjeta de agradecimiento (para que cada entrega genere una recomendación)', 'Plantilla: Cartelito de horarios (para que no lleguen sin avisar)', 'Checklist "Mi estación pro en 10 pasos" (imprimible)', 'Checklist "Entrega que se ve profesional" (imprimible)', 'Mini guía de fotos para que tu trabajo se vea caro en redes sin ser fotógrafa'],
  closingLine: 'Porque la primera impresión no la das cuando coses. La das cuando entregas.'
}];
function OrderBump({
  icon,
  title,
  description,
  price,
  imageSrc,
  imageAlt,
  checked,
  onToggle
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  price: string;
  imageSrc: string;
  imageAlt: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return <motion.div className="rounded-xl overflow-hidden text-left cursor-pointer select-none" style={{
    backgroundColor: '#FAF9F6',
    border: checked ? '2px solid #B5935B' : '1.5px solid #D4A017',
    boxShadow: checked ? '0 0 0 3px rgba(181,147,91,0.2), 0 4px 16px rgba(212,160,23,0.12)' : '0 2px 12px rgba(212,160,23,0.1)',
    transition: 'box-shadow 0.2s ease, border 0.2s ease'
  }} onClick={onToggle} whileTap={{
    scale: 0.995
  }} data-id="element-143">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5" style={{
      backgroundColor: '#D4A017'
    }} data-id="element-144">
        <div className="flex items-center gap-2" data-id="element-145">
          <span style={{
          color: '#1A1A1A'
        }} data-id="element-146">
            {icon}
          </span>
          <div className="flex items-center gap-2 flex-wrap" data-id="element-147">
            <span className="font-body text-xs font-bold uppercase tracking-widest" style={{
            color: '#1A1A1A'
          }} data-id="element-148">
              Mejora Opcional — Por solo {price}
            </span>
            <span className="font-body text-xs font-semibold px-2 py-0.5 rounded-full" style={{
            backgroundColor: 'rgba(0,0,0,0.15)',
            color: '#1A1A1A',
            border: '1px solid rgba(0,0,0,0.2)'
          }} data-id="element-149">
              ★ Altamente recomendado
            </span>
          </div>
        </div>
        {/* Flash sale badge */}
        <motion.div className="flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0" style={{
        backgroundColor: 'rgba(0,0,0,0.18)',
        border: '1px solid rgba(0,0,0,0.2)'
      }} animate={{
        opacity: [1, 0.6, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-150">
          <ZapIcon className="w-3 h-3" style={{
          color: '#1A1A1A'
        }} aria-hidden="true" data-id="element-151" />
          <span className="font-body text-xs font-bold" style={{
          color: '#1A1A1A'
        }} data-id="element-152">
            Oferta Flash
          </span>
        </motion.div>
      </div>

      {/* Body */}
      <div className="flex gap-4 p-4 md:p-5" data-id="element-153">
        {/* Custom checkbox */}
        <div className="flex-shrink-0 mt-1" data-id="element-154">
          <div className="w-5 h-5 rounded flex items-center justify-center transition-all duration-200" style={{
          border: checked ? '2px solid #B5935B' : '1.5px solid #B5935B',
          backgroundColor: checked ? '#B5935B' : 'transparent'
        }} data-id="element-155">
            {checked && <svg width="11" height="8" viewBox="0 0 11 8" fill="none" data-id="element-156">
                <path d="M1 4L4 7L10 1" stroke="#FAF9F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-157" />
              </svg>}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-158">
          <h4 className="font-serif-display font-bold text-base md:text-lg leading-snug mb-2" style={{
          color: '#1A233A'
        }} data-id="element-159">
            {title}
          </h4>
          <p className="font-body text-sm leading-relaxed" style={{
          color: '#4A4A4A',
          lineHeight: '1.7'
        }} data-id="element-160">
            {description}
          </p>
          <p className="font-serif-display font-bold text-base mt-3" style={{
          color: '#B5935B'
        }} data-id="element-161">
            Agregar por solo {price}
          </p>
        </div>

        {/* Image */}
        <div className="flex-shrink-0 w-20 md:w-28" data-id="element-162">
          <img src={imageSrc} alt={imageAlt} className="w-full rounded-lg object-cover" style={{
          aspectRatio: '3/4'
        }} data-id="element-163" />
        </div>
      </div>
    </motion.div>;
}
export function PricingCTA({
  bump1,
  bump2,
  setBump1,
  setBump2,
  totalPrice
}: {
  bump1: boolean;
  bump2: boolean;
  setBump1: (v: boolean | ((prev: boolean) => boolean)) => void;
  setBump2: (v: boolean | ((prev: boolean) => boolean)) => void;
  totalPrice: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [expandedBonuses, setExpandedBonuses] = useState<Record<number, boolean>>({});
  const toggleBonus = (index: number) => {
    setExpandedBonuses(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const hasExtras = bump1 || bump2;
  const checkoutUrl = getCheckoutUrl(bump1, bump2);
  return <section id="pricing" className="w-full" style={{
    backgroundColor: '#0B1A2E'
  }} data-id="element-164">
      {/* Top stitch */}
      <div style={{
      borderTop: '3px dashed #D4A017',
      opacity: 0.3
    }} data-id="element-165" />

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-18 text-center" ref={ref} data-id="element-166">
        {/* Objection handling */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-167">
          <p className="font-body text-lg md:text-xl leading-relaxed mb-4" style={{
          color: 'rgba(250, 243, 232, 0.8)'
        }} data-id="element-168">
            No necesitas un diploma.
          </p>
          <p className="font-body text-lg md:text-xl leading-relaxed mb-4" style={{
          color: 'rgba(250, 243, 232, 0.8)'
        }} data-id="element-169">
            No necesitas una máquina industrial.
          </p>
          <p className="font-serif-display font-bold text-2xl md:text-3xl leading-snug mb-8" style={{
          color: '#FAF3E8'
        }} data-id="element-170">
            Solo necesitas este manual y las ganas de no volver a preocuparte
            por el dinero a fin de mes.
          </p>
        </motion.div>

        {/* Stitch */}
        <hr className="stitch-divider mb-8" data-id="element-171" />

        {/* Price — dynamic */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={isInView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-172">
          <p className="font-body text-sm uppercase tracking-widest mb-3" style={{
          color: '#D4A017'
        }} data-id="element-173">
            Precio especial
          </p>
          <div className="flex items-center justify-center gap-4 mb-2" data-id="element-174">
            <span className="font-serif-display font-bold text-3xl md:text-4xl line-through" style={{
            color: 'rgba(250, 243, 232, 0.35)'
          }} data-id="element-175">
              $27
            </span>
            <span className="inline-block px-3 py-1 rounded-lg text-sm font-bold font-body" style={{
            backgroundColor: '#C0392B',
            color: '#fff'
          }} data-id="element-176">
              -36% OFF
            </span>
          </div>

          {/* Dynamic big price */}
          <div className="flex items-end justify-center gap-3 mb-4" data-id="element-177">
            <AnimatePresence mode="wait" data-id="element-178">
              {hasExtras && <motion.span key="base-strikethrough" initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className="font-serif-display font-bold text-4xl md:text-5xl line-through mb-2" style={{
              color: 'rgba(250, 243, 232, 0.25)'
            }} data-id="element-179">
                  $17.27
                </motion.span>}
            </AnimatePresence>
            <motion.span key={totalPrice} initial={{
            scale: 1.08,
            color: '#FFD700'
          }} animate={{
            scale: 1,
            color: '#D4A017'
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="font-serif-display font-bold text-7xl md:text-8xl" data-id="element-180">
              ${totalPrice.toFixed(2)}
            </motion.span>
          </div>

          <p className="font-serif-display italic text-lg md:text-xl mb-8" style={{
          color: 'rgba(250, 243, 232, 0.55)'
        }} data-id="element-181">
            (Un solo ruedo de pantalón que hagas hoy paga este libro)
          </p>
        </motion.div>

        {/* CTA Button — dynamic */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-182">
          <motion.a href={checkoutUrl} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="cta-glow inline-flex items-center justify-center gap-3 px-10 py-5 rounded-xl font-bold text-xl font-body transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95" style={{
          backgroundColor: '#D4A017',
          color: '#1A1A1A'
        }} whileHover={{
          scale: 1.04
        }} whileTap={{
          scale: 0.96
        }} aria-label={`Descarga el Método Express por $${totalPrice.toFixed(2)}`} data-id="element-183">
            Descarga el Método Express —{' '}
            <AnimatePresence mode="wait" data-id="element-184">
              <motion.span key={totalPrice} initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.2
            }} className="font-bold" data-id="element-185">
                ${totalPrice.toFixed(2)}
              </motion.span>
            </AnimatePresence>
          </motion.a>

          {/* Micro-copy */}
          <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1.5" data-id="element-186">
            {['Descarga inmediata', '3 bonos incluidos', 'Garantía 30 días', 'Sin suscripción'].map(item => <span key={item} className="flex items-center gap-1.5 text-sm font-body" style={{
            color: 'rgba(250, 243, 232, 0.45)'
          }} data-id="element-187">
                <CheckCircle2Icon className="w-3.5 h-3.5" style={{
              color: '#D4A017'
            }} aria-hidden="true" data-id="element-188" />
                {item}
              </span>)}
          </div>

          {/* Social proof */}
          <div className="mt-4 flex items-center justify-center gap-2" data-id="element-189">
            <div className="flex flex-col" data-id="element-190">
              <div className="flex items-center gap-1" data-id="element-191">
                <div className="flex items-center gap-0.5" data-id="element-192">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-sm" style={{
                  color: '#D4A017'
                }} data-id="element-193">
                      ★
                    </span>)}
                </div>
                <span className="font-body text-xs" style={{
                color: 'rgba(250,243,232,0.5)'
              }} data-id="element-194">
                  +1,200 mujeres ya lo están logrando
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== ORDER BUMPS ===== */}
        <motion.div className="mt-10 space-y-4 max-w-xl mx-auto" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-195">
          <p className="font-body text-xs uppercase tracking-widest mb-4" style={{
          color: 'rgba(250, 243, 232, 0.35)'
        }} data-id="element-196">
            Complementa tu pedido
          </p>

          <OrderBump icon={<LockIcon className="w-4 h-4" aria-hidden="true" data-id="element-198" />} title="Eleva tu Estándar: La Guía Maestra de Insumos Élite en USA" description="No permitas que un hilo de baja calidad arruine una prenda costosa. Glosario bilingüe de insumos profesionales, proveedores industriales y protocolo de compatibilidad textil para que cada puntada respire lujo." price="$9" imageSrc="https://cdn.magicpatterns.com/uploads/mbyHHxDGFNr6PGwHV3ik9S/heylloyd-image-1771867124884.png" imageAlt="The Material Standard — Guía Maestra de Insumos Élite en USA" checked={bump1} onToggle={() => setBump1(v => !v)} data-id="element-197" />

          <OrderBump icon={<BriefcaseIcon className="w-4 h-4" aria-hidden="true" data-id="element-200" />} title="Autoridad Inmediata: Tu Sistema de Gestión y Protocolo Boutique" description="Elimina el caos de las notas en papeles sueltos. Recibos digitales para WhatsApp, formularios de recepción de prendas y rastreador de entregas para proyectar una imagen de estudio profesional desde el primer día." price="$9" imageSrc="https://cdn.magicpatterns.com/uploads/2qTFMk56mAZyA9sJuAAAyS/heylloyd-image-1771867497813.png" imageAlt="The Professional Ledger — Sistema de Gestión y Protocolo Boutique" checked={bump2} onToggle={() => setBump2(v => !v)} data-id="element-199" />
        </motion.div>

        {/* ===== WHAT'S INCLUDED — VALUE STACK (below order bumps, dynamic total) ===== */}
        <motion.div className="mt-10" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.35
      }} data-id="element-201">
          <p className="font-body text-sm uppercase tracking-widest mb-5" style={{
          color: '#D4A017'
        }} data-id="element-202">
            Lo que te llevas hoy
          </p>

          <div className="space-y-3 max-w-md mx-auto text-left mb-6" data-id="element-203">
            {/* Main product */}
            <div className="flex items-center justify-between gap-3" data-id="element-204">
              <div className="flex items-center gap-3" data-id="element-205">
                <BookOpenIcon className="w-5 h-5 flex-shrink-0" style={{
                color: '#D4A017'
              }} aria-hidden="true" data-id="element-206" />
                <span className="font-body text-sm md:text-base" style={{
                color: 'rgba(250, 243, 232, 0.85)'
              }} data-id="element-207">
                  Método Express: 7 Arreglos que Pagan la Renta
                </span>
              </div>
              <span className="font-body text-sm font-semibold flex-shrink-0" style={{
              color: 'rgba(250, 243, 232, 0.5)'
            }} data-id="element-208">
                $14.97
              </span>
            </div>

            {/* Bonus 1 */}
            <div data-id="element-209">
              <div className="flex items-center justify-between gap-3" data-id="element-210">
                <div className="flex items-center gap-3" data-id="element-211">
                  <GiftIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#D4A017'
                }} aria-hidden="true" data-id="element-212" />
                  <span className="font-body text-sm md:text-base" style={{
                  color: 'rgba(250, 243, 232, 0.85)'
                }} data-id="element-213">
                    Bono #1: Kit Anti-Errores (Cero Pánico)
                  </span>
                </div>
                <span className="font-body text-sm font-semibold flex-shrink-0" style={{
                color: 'rgba(250, 243, 232, 0.5)'
              }} data-id="element-214">
                  Gratis
                </span>
              </div>
              <button onClick={() => toggleBonus(0)} className="flex items-center gap-1 mt-1.5 ml-8 font-body text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded" style={{
              color: '#D4A017'
            }} data-id="element-215">
                {expandedBonuses[0] ? 'Leer menos' : 'Leer más'}
                <ChevronDownIcon className="w-3.5 h-3.5 transition-transform duration-200" style={{
                transform: expandedBonuses[0] ? 'rotate(180deg)' : 'rotate(0deg)'
              }} data-id="element-216" />
              </button>
              <AnimatePresence data-id="element-217">
                {expandedBonuses[0] && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden ml-8" data-id="element-218">
                    <div className="pt-3 pb-1" data-id="element-219">
                      <p className="font-body text-sm leading-relaxed mb-3" style={{
                    color: 'rgba(250, 243, 232, 0.65)'
                  }} data-id="element-220">
                        {bonusDetails[0].intro}
                      </p>
                      <ul className="space-y-2 mb-3" data-id="element-221">
                        {bonusDetails[0].bullets.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-222">
                            <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#D4A017'
                      }} aria-hidden="true" data-id="element-223" />
                            <span className="font-body text-sm leading-relaxed" style={{
                        color: 'rgba(250, 243, 232, 0.7)'
                      }} data-id="element-224">
                              {b}
                            </span>
                          </li>)}
                      </ul>
                      <p className="font-serif-display italic text-sm" style={{
                    color: 'rgba(250, 243, 232, 0.45)'
                  }} data-id="element-225">
                        {bonusDetails[0].closingLine}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Bonus 2 */}
            <div data-id="element-226">
              <div className="flex items-center justify-between gap-3" data-id="element-227">
                <div className="flex items-center gap-3" data-id="element-228">
                  <GiftIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#D4A017'
                }} aria-hidden="true" data-id="element-229" />
                  <span className="font-body text-sm md:text-base" style={{
                  color: 'rgba(250, 243, 232, 0.85)'
                }} data-id="element-230">
                    Bono #2: Scripts de Confianza (Sin Pena)
                  </span>
                </div>
                <span className="font-body text-sm font-semibold flex-shrink-0" style={{
                color: 'rgba(250, 243, 232, 0.5)'
              }} data-id="element-231">
                  Gratis
                </span>
              </div>
              <button onClick={() => toggleBonus(1)} className="flex items-center gap-1 mt-1.5 ml-8 font-body text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded" style={{
              color: '#D4A017'
            }} data-id="element-232">
                {expandedBonuses[1] ? 'Leer menos' : 'Leer más'}
                <ChevronDownIcon className="w-3.5 h-3.5 transition-transform duration-200" style={{
                transform: expandedBonuses[1] ? 'rotate(180deg)' : 'rotate(0deg)'
              }} data-id="element-233" />
              </button>
              <AnimatePresence data-id="element-234">
                {expandedBonuses[1] && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden ml-8" data-id="element-235">
                    <div className="pt-3 pb-1" data-id="element-236">
                      <p className="font-body text-sm leading-relaxed mb-3" style={{
                    color: 'rgba(250, 243, 232, 0.65)'
                  }} data-id="element-237">
                        {bonusDetails[1].intro}
                      </p>
                      <ul className="space-y-2 mb-3" data-id="element-238">
                        {bonusDetails[1].bullets.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-239">
                            <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#D4A017'
                      }} aria-hidden="true" data-id="element-240" />
                            <span className="font-body text-sm leading-relaxed" style={{
                        color: 'rgba(250, 243, 232, 0.7)'
                      }} data-id="element-241">
                              {b}
                            </span>
                          </li>)}
                      </ul>
                      <p className="font-serif-display italic text-sm" style={{
                    color: 'rgba(250, 243, 232, 0.45)'
                  }} data-id="element-242">
                        {bonusDetails[1].closingLine}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Bonus 3 */}
            <div data-id="element-243">
              <div className="flex items-center justify-between gap-3" data-id="element-244">
                <div className="flex items-center gap-3" data-id="element-245">
                  <GiftIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#D4A017'
                }} aria-hidden="true" data-id="element-246" />
                  <span className="font-body text-sm md:text-base" style={{
                  color: 'rgba(250, 243, 232, 0.85)'
                }} data-id="element-247">
                    Bono #3: Mini-Brand Express (30 Min)
                  </span>
                </div>
                <span className="font-body text-sm font-semibold flex-shrink-0" style={{
                color: 'rgba(250, 243, 232, 0.5)'
              }} data-id="element-248">
                  Gratis
                </span>
              </div>
              <button onClick={() => toggleBonus(2)} className="flex items-center gap-1 mt-1.5 ml-8 font-body text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded" style={{
              color: '#D4A017'
            }} data-id="element-249">
                {expandedBonuses[2] ? 'Leer menos' : 'Leer más'}
                <ChevronDownIcon className="w-3.5 h-3.5 transition-transform duration-200" style={{
                transform: expandedBonuses[2] ? 'rotate(180deg)' : 'rotate(0deg)'
              }} data-id="element-250" />
              </button>
              <AnimatePresence data-id="element-251">
                {expandedBonuses[2] && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden ml-8" data-id="element-252">
                    <div className="pt-3 pb-1" data-id="element-253">
                      <p className="font-body text-sm leading-relaxed mb-3" style={{
                    color: 'rgba(250, 243, 232, 0.65)'
                  }} data-id="element-254">
                        {bonusDetails[2].intro}
                      </p>
                      <ul className="space-y-2 mb-3" data-id="element-255">
                        {bonusDetails[2].bullets.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-256">
                            <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#D4A017'
                      }} aria-hidden="true" data-id="element-257" />
                            <span className="font-body text-sm leading-relaxed" style={{
                        color: 'rgba(250, 243, 232, 0.7)'
                      }} data-id="element-258">
                              {b}
                            </span>
                          </li>)}
                      </ul>
                      <p className="font-serif-display italic text-sm" style={{
                    color: 'rgba(250, 243, 232, 0.45)'
                  }} data-id="element-259">
                        {bonusDetails[2].closingLine}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Order Bump 1 — appears when selected */}
            <AnimatePresence data-id="element-260">
              {bump1 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-261">
                  <div className="flex items-center justify-between gap-3" data-id="element-262">
                    <div className="flex items-center gap-3" data-id="element-263">
                      <LockIcon className="w-5 h-5 flex-shrink-0" style={{
                    color: '#B5935B'
                  }} aria-hidden="true" data-id="element-264" />
                      <span className="font-body text-sm md:text-base" style={{
                    color: 'rgba(250, 243, 232, 0.85)'
                  }} data-id="element-265">
                        The Material Standard (Insumos Élite)
                      </span>
                    </div>
                    <span className="font-body text-sm font-semibold flex-shrink-0" style={{
                  color: '#B5935B'
                }} data-id="element-266">
                      +$9
                    </span>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Order Bump 2 — appears when selected */}
            <AnimatePresence data-id="element-267">
              {bump2 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-268">
                  <div className="flex items-center justify-between gap-3" data-id="element-269">
                    <div className="flex items-center gap-3" data-id="element-270">
                      <BriefcaseIcon className="w-5 h-5 flex-shrink-0" style={{
                    color: '#B5935B'
                  }} aria-hidden="true" data-id="element-271" />
                      <span className="font-body text-sm md:text-base" style={{
                    color: 'rgba(250, 243, 232, 0.85)'
                  }} data-id="element-272">
                        The Professional Ledger (Gestión Boutique)
                      </span>
                    </div>
                    <span className="font-body text-sm font-semibold flex-shrink-0" style={{
                  color: '#B5935B'
                }} data-id="element-273">
                      +$9
                    </span>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Dynamic total */}
          <motion.div className="max-w-md mx-auto rounded-lg overflow-hidden" style={{
          border: '1px solid rgba(212, 160, 23, 0.25)'
        }} layout data-id="element-274">
            <div className="flex items-center justify-between gap-3 px-4 py-3" style={{
            backgroundColor: 'rgba(212, 160, 23, 0.08)'
          }} data-id="element-275">
              <span className="font-body text-sm font-bold uppercase tracking-wide" style={{
              color: '#D4A017'
            }} data-id="element-276">
                Tu pedido hoy
              </span>
              <motion.span key={totalPrice} initial={{
              scale: 1.15,
              color: '#FFD700'
            }} animate={{
              scale: 1,
              color: '#D4A017'
            }} transition={{
              duration: 0.3
            }} className="font-serif-display font-bold text-2xl" data-id="element-277">
                ${totalPrice.toFixed(2)}
              </motion.span>
            </div>
            {(bump1 || bump2) && <div className="px-4 py-2" style={{
            backgroundColor: 'rgba(181, 147, 91, 0.06)',
            borderTop: '1px solid rgba(181,147,91,0.15)'
          }} data-id="element-278">
                <p className="font-body text-xs text-center" style={{
              color: 'rgba(250, 243, 232, 0.4)'
            }} data-id="element-279">
                  Incluye{' '}
                  {[bump1 && 'Material Standard', bump2 && 'Professional Ledger'].filter(Boolean).join(' + ')}
                </p>
              </div>}

            {/* ===== DYNAMIC CHECKOUT BUTTON ===== */}
            <div className="px-4 py-4" style={{
            borderTop: '1px solid rgba(212,160,23,0.15)'
          }} data-id="element-280">
              <motion.a href={checkoutUrl} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="cta-glow inline-flex items-center justify-center gap-3 px-10 py-5 rounded-xl font-bold text-xl font-body transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95" style={{
              backgroundColor: '#D4A017',
              color: '#1A1A1A'
            }} whileHover={{
              scale: 1.04
            }} whileTap={{
              scale: 0.96
            }} aria-label={`Descarga el Método Express por $${totalPrice.toFixed(2)}`} data-id="element-281">
                Descarga el Método Express —{' '}
                <AnimatePresence mode="wait" data-id="element-282">
                  <motion.span key={totalPrice} initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 6
                }} transition={{
                  duration: 0.2
                }} className="font-bold" data-id="element-283">
                    ${totalPrice.toFixed(2)}
                  </motion.span>
                </AnimatePresence>
              </motion.a>
              <p className="font-body text-xs text-center mt-2" style={{
              color: 'rgba(250, 243, 232, 0.35)'
            }} data-id="element-284">
                Pago seguro · Descarga inmediata · Garantía 30 días
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom decorative thread */}
        <div className="mt-10" data-id="element-285">
          <hr className="stitch-divider-wide" data-id="element-286" />
        </div>
      </div>
    </section>;
}